import React, { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { App, Button, Dropdown, Modal } from 'antd';

import { api } from '../../api/client';
import { API_ELEMENT_SET_PROMOTE_URL, API_ELEMENT_SET_URL, isAdmin } from '../../utils';
import { KEBAB, PLUS } from '../icons';
import { normalizeId } from '../../utils/ids';
import { formatCount } from '../../utils/counts';
import { RecordIdentity, RecordTitle } from '../common/RecordBadge';
import GroupHeader from '../common/GroupHeader';
import { buildActionMenu } from '../common/actionMenu';
import { cardsAttachingSet, elementSetPath, newElementSetPath } from '../../utils/elementSets';
import { useCardDefinitions } from '../../contexts/CardDefinitionsContext';
import { confirmDestructive } from '../common/confirmDestructive';
import { DataState, Skeleton, deriveDataState } from '../loading';

/**
 * The ELEMENT SETS section of a cards library (docs/plans/element-sets-and-me.md):
 * global sets first, then the collection's own — each row the set's plain
 * title, "used by N cards" (counted over the readable cards that attach it,
 * the API's own usage rule) and a ⋯ of Open · Promote to global (admin) ·
 * Delete (managers of the owning collection; admins for global). "Create an
 * element set" sits beside the library's "Create a card".
 *
 * With a `collectionId` the section is that collection's (its own sets and
 * the global ones a card of it may attach); without one it is the cards
 * index's (every set the caller can read, grouped Global · Your element
 * sets). Both read the app-wide cache and refresh it after a mutation.
 */
export default function ElementSetList({
	collectionId = null,
	// With a `collectionId`: does the viewer manage THAT collection?
	canManage = false,
	// Without one (the cards index): the ids of the collections the viewer
	// manages, so a collection set's Edit and Delete are offered on the same
	// terms the library offers them — its managers', and admins'.
	manageableCollectionIds = null,
	// A Map of set id -> the name of the Discover bundle that brought it
	// (the Card Library passes it, KPR-1130): the row says "From <bundle>".
	bundleOf = null,
}) {
	const navigate = useNavigate();
	const { message } = App.useApp();
	const { cards = [], elementSets = [], loading, error, refreshElementSets, refresh } = useCardDefinitions() || {};
	const refreshSets = refreshElementSets || refresh;
	const admin = isAdmin();
	const scopedTo = normalizeId(collectionId);
	const manageable = useMemo(
		() => new Set([...(manageableCollectionIds || [])].map(normalizeId).filter(Boolean)),
		[manageableCollectionIds]
	);

	const rows = useMemo(() => {
		const list = (elementSets || []).filter(set => {
			if (set?.scope === 'global') return true;
			if (!scopedTo) return true;
			return normalizeId(set?.collection_id) === scopedTo;
		});
		const byName = (a, b) => (a?.name || '').localeCompare(b?.name || '', undefined, { sensitivity: 'base' });
		const toRow = set => ({
			id: normalizeId(set._id),
			set,
			name: set?.name || '(untitled set)',
			global: set?.scope === 'global',
			owned: !!scopedTo && normalizeId(set?.collection_id) === scopedTo,
			usedBy: cardsAttachingSet(set, cards).length,
			bundle: bundleOf ? bundleOf.get(normalizeId(set._id)) || null : null,
		});
		return {
			global: list.filter(set => set?.scope === 'global').sort(byName).map(toRow),
			yours: list.filter(set => set?.scope !== 'global').sort(byName).map(toRow),
		};
	}, [elementSets, cards, scopedTo, bundleOf]);

	const deleteSet = useCallback((row) => {
		confirmDestructive(Modal, {
			title: `Delete the "${row.name}" element set?`,
			content: 'Refused while any card still attaches it. Values already stored on items are not deleted: they stay on the items, hidden. History can restore the set.',
			okText: 'Delete',
			onOk: async () => {
				try {
					await api.delete(API_ELEMENT_SET_URL(row.id));
					message.success('Element set deleted.');
					refreshSets?.();
				} catch (err) {
					const data = err?.response?.data;
					message.error(data?.code === 'element_set_in_use'
						? `Still attached to ${(data.cards || []).map(card => card?.name).filter(Boolean).join(', ') || 'a card'}. Remove it from each card first.`
						: (data?.message || 'Delete failed.'));
				}
			},
		});
	}, [message, refreshSets]);

	const promoteSet = useCallback((row) => {
		Modal.confirm({
			title: `Promote "${row.name}" to a global element set?`,
			content: 'Every keepr user will be able to attach it. Its key and every stored element name stay as they are. This cannot be undone from the app.',
			okText: 'Promote',
			onOk: async () => {
				try {
					await api.post(API_ELEMENT_SET_PROMOTE_URL(row.id));
					message.success('Element set promoted to global.');
					refreshSets?.();
				} catch (err) {
					const data = err?.response?.data;
					message.error(data?.code === 'element_set_key_taken'
						? `A global element set already uses the key "${data.key || row.set?.key}".`
						: (data?.message || 'Promote failed.'));
				}
			},
		});
	}, [message, refreshSets]);

	// Who may edit and delete (the API's assertSetWritable): admins anywhere;
	// a collection's managers for its own — the collection in hand, or, on
	// the cards index, any collection the viewer manages.
	const canWrite = row => {
		if (admin) return true;
		if (row.global) return false;
		return scopedTo ? (row.owned && canManage) : manageable.has(normalizeId(row.set?.collection_id));
	};

	const rowMenu = useCallback((row) => buildActionMenu([
		{ key: 'open', group: 'navigate', glyph: 'open', label: 'Open element set', onClick: () => navigate(elementSetPath(row.set)) },
		canWrite(row) ? {
			key: 'edit', group: 'navigate', glyph: 'edit', label: 'Edit element set', onClick: () => navigate(`${elementSetPath(row.set)}?tab=basics`),
		} : null,
		(!row.global && admin) ? { key: 'promote', group: 'record', glyph: 'collection', label: 'Promote to global', onClick: () => promoteSet(row) } : null,
		canWrite(row) ? { key: 'delete', group: 'destructive', glyph: 'delete', label: 'Delete element set', onClick: () => deleteSet(row) } : null,
	]), [admin, canManage, scopedTo, manageable, navigate, promoteSet, deleteSet]); // eslint-disable-line react-hooks/exhaustive-deps

	const renderRows = list => (
		<div className='card-library__grid'>
			{list.map(row => (
				<RecordIdentity
					key={row.id}
					className='card-library__row element-set-list__row'
					badge={<RecordTitle kind='elementSet' record={row.set} title={row.name} wrap />}
					below={(
						<span>
							{[
								row.global ? 'Global' : (scopedTo ? 'This collection' : 'Collection element set'),
								row.bundle ? `From ${row.bundle}` : null,
								formatCount((row.set?.elements || []).length, 'element'),
								`used by ${formatCount(row.usedBy, 'card')}`,
							].filter(Boolean).join(' · ')}
						</span>
					)}
					actions={(
						<Dropdown trigger={['click']} placement='bottomRight' menu={rowMenu(row)}>
							<Button type='text' className='card-library__action' aria-label={`${row.name} actions`} icon={<KEBAB className='medium-icon' />} />
						</Dropdown>
					)}
				/>
			))}
		</div>
	);

	const total = rows.global.length + rows.yours.length;
	const canCreate = scopedTo ? canManage : admin;

	return (
		<div className='card-library__section element-set-list' data-testid='element-set-list'>
			<div className='card-library__browse-row'>
				<GroupHeader as='h4' label='Element sets' count={total} countLabel='set' />
				{canCreate ? (
					<Button
						type='link'
						size='small'
						className='card-library__link-action'
						icon={<PLUS />}
						onClick={() => navigate(newElementSetPath(scopedTo))}
					>
						Create an element set
					</Button>
				) : null}
			</div>
			<p className='card-library__hint'>
				A reusable set of elements any card can attach. Its fields land on the card's items in a panel named for the set. Global sets are keepr's; a collection's own sets are its managers'.
			</p>
			<DataState
				state={deriveDataState({ loading, error: loading ? null : error, hasData: total > 0, isEmpty: total === 0 })}
				hasData={total > 0}
				isEmpty={total === 0}
				skeleton={<Skeleton variant='list-row' count={2} />}
				empty={(
					<div className='card-library__empty' data-testid='element-set-list-empty'>
						<p>{ canCreate ? 'No element sets yet. Create one to reuse a group of elements across cards.' : 'No element sets yet. A manager of this collection can create one.' }</p>
						{ canCreate ? (
							<Button type='default' icon={<PLUS />} onClick={() => navigate(newElementSetPath(scopedTo))}>
								Create an element set
							</Button>
						) : null }
					</div>
				)}
				onRetry={refreshSets}
				label='element sets'
			>
				{rows.global.length ? <>
					{rows.yours.length ? <div className='micro-label card-home__group-label'>Global</div> : null}
					{renderRows(rows.global)}
				</> : null}
				{rows.yours.length ? <>
					<div className='micro-label card-home__group-label'>{scopedTo ? 'This collection' : 'Your element sets'}</div>
					{renderRows(rows.yours)}
				</> : null}
			</DataState>
		</div>
	);
}
